import React, { useMemo, useState } from 'react';
import { Badge, Button, ConfirmDialog, Drawer, EmptyState, Input, Select, Spinner, Switch, Textarea, useToast } from '../../ui/components';
import { IconButton } from '../../ui/components/Button';
import { Icon } from '../../ui/icons';
import { useT } from '../../i18n';
import { catalogApi } from './catalogApi';

const EMPTY = { name: '', code: '', parentId: '', description: '', sortOrder: 0, isActive: true };

export default function CategoriesTab({ categories, loading, canManage, onChanged }) {
  const toast = useToast();
  const t = useT();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const byId = useMemo(() => Object.fromEntries(categories.map((c) => [c.id, c])), [categories]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setDrawerOpen(true);
  };
  const openEdit = (c) => {
    setEditing(c);
    setForm({
      name: c.name ?? '',
      code: c.code ?? '',
      parentId: c.parentId ?? '',
      description: c.description ?? '',
      sortOrder: c.sortOrder ?? 0,
      isActive: c.isActive ?? true,
    });
    setDrawerOpen(true);
  };

  const parentOptions = useMemo(
    () => [
      { value: '', label: t('— Top level —') },
      ...categories.filter((c) => !editing || c.id !== editing.id).map((c) => ({ value: c.id, label: c.name })),
    ],
    [categories, editing, t],
  );

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSubmitting(true);
    try {
      const payload = {
        name: form.name.trim(),
        code: form.code.trim() || null,
        parentId: form.parentId || null,
        description: form.description.trim() || null,
        sortOrder: Number(form.sortOrder) || 0,
        isActive: form.isActive,
      };
      if (editing) await catalogApi.updateCategory(editing.id, { id: editing.id, ...payload });
      else await catalogApi.createCategory(payload);
      toast.success(editing ? t('Category updated') : t('Category created'));
      setDrawerOpen(false);
      await onChanged?.();
    } catch (err) {
      toast.error(t('Could not save category'), { description: err?.message });
    } finally {
      setSubmitting(false);
    }
  };

  const doDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await catalogApi.removeCategory(deleteTarget.id);
      toast.success(t('Category deleted'));
      setDeleteTarget(null);
      await onChanged?.();
    } catch (err) {
      toast.error(t('Could not delete category'), { description: err?.message });
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return <div style={{ display: 'grid', placeItems: 'center', padding: 'var(--space-12)' }}><Spinner size={24} /></div>;
  }

  return (
    <>
      <div className="pp-toolbar">
        <div className="pp-toolbar__spacer" />
        {canManage && (
          <Button variant="primary" leadingIcon={<Icon.Plus size={16} />} onClick={openCreate}>
            {t('New category')}
          </Button>
        )}
      </div>

      {categories.length === 0 ? (
        <EmptyState
          icon={<Icon.Folders size={28} />}
          title={t('No categories yet')}
          description={t('Group items into categories to organise the catalog and reporting.')}
          action={canManage ? <Button variant="primary" leadingIcon={<Icon.Plus size={16} />} onClick={openCreate}>{t('New category')}</Button> : null}
        />
      ) : (
        <div className="cat-list">
          {categories.map((c) => (
            <div className="cat-row" key={c.id}>
              <span className="cat-row__icon"><Icon.Folder size={18} /></span>
              <div className="cat-row__main">
                <span className="cat-row__name">
                  {c.name}
                  {!c.isActive && <Badge tone="neutral">{t('Inactive')}</Badge>}
                </span>
                <span className="cat-row__meta">
                  {c.code ? `${c.code} · ` : ''}
                  {c.parentId && byId[c.parentId] ? t('in {name}', { name: byId[c.parentId].name }) : t('Top level')}
                </span>
              </div>
              {canManage && (
                <div className="cat-row__actions">
                  <IconButton aria-label={t('Edit category')} variant="ghost" size="icon-sm" onClick={() => openEdit(c)}>
                    <Icon.Pencil size={15} />
                  </IconButton>
                  <IconButton aria-label={t('Delete category')} variant="ghost" size="icon-sm" onClick={() => setDeleteTarget(c)}>
                    <Icon.Trash size={15} />
                  </IconButton>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Drawer
        open={drawerOpen}
        onClose={() => (submitting ? null : setDrawerOpen(false))}
        title={editing ? t('Edit category') : t('New category')}
        description={t('Group items for organisation and reporting. Categories can be nested under a parent.')}
        size="md"
      >
        <form className="pp-form" onSubmit={save}>
          <Input label={t('Name')} value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} autoFocus />
          <div className="pp-form__row">
            <Input label={t('Code')} value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))} />
            <Input label={t('Sort order')} type="number" value={form.sortOrder} onChange={(e) => setForm((f) => ({ ...f, sortOrder: e.target.value }))} />
          </div>
          <div className="ps-field">
            <span className="ps-field__label">{t('Parent category')}</span>
            <Select value={form.parentId} onChange={(v) => setForm((f) => ({ ...f, parentId: v }))} options={parentOptions} ariaLabel={t('Parent category')} searchable />
          </div>
          <Textarea label={t('Description')} rows={2} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
          <Switch checked={form.isActive} onChange={(v) => setForm((f) => ({ ...f, isActive: v }))} label={t('Active')} />
          <div className="pp-form__actions">
            <Button type="button" variant="ghost" onClick={() => setDrawerOpen(false)} disabled={submitting}>{t('Cancel')}</Button>
            <Button type="submit" variant="primary" loading={submitting}>{editing ? t('Save changes') : t('Create category')}</Button>
          </div>
        </form>
      </Drawer>

      <ConfirmDialog
        open={!!deleteTarget}
        title={t('Delete category')}
        description={deleteTarget ? t('Delete "{name}"? Items must be reassigned first.', { name: deleteTarget.name }) : ''}
        confirmLabel={t('Delete')}
        tone="danger"
        loading={deleting}
        onConfirm={doDelete}
        onCancel={() => (deleting ? null : setDeleteTarget(null))}
      />
    </>
  );
}
